import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { useLocale, useT } from '../i18n/locale-context';
import {
  fetchSavedCard,
  fetchPlans,
  setAutoRenew,
  removeSavedCard,
  pauseSubscription,
  resumeSubscription,
  type ChurnReasonInput,
} from '../lib/billing-client';
import { track } from '../lib/funnel';
import { formatBudget, formatDeadline } from '../lib/format';
import { CHURN_REASON_CHANGING_CARD, type ChurnReasonKind } from '../lib/churn-reasons';
import { fetchNewMatchCount } from '../lib/my-tenders-client';
import { Checkbox } from './ui/checkbox';
import { Spinner } from './ui/spinner';
import { CardSaveModal } from './card-save-modal';
import { ChurnReasonModal } from './churn-reason-modal';
import { countryConfig } from '../country';

/**
 * Kazakhstan: the subscription and its card live at Stripe. The panel keeps
 * what is true there — the next charge, the card Stripe holds, the auto-renew
 * switch (the server moves it at Stripe) — and drops everything that opens the
 * Uzbek Payme card sheet: «Изменить карту», «Обновить карту», «Удалить карту»
 * (whose reason sheet offers «Меняю карту» → that sheet) and «Привязать карту».
 */
const STRIPE_CHECKOUT = countryConfig.payments.checkout === 'stripe';

const CARD_QUERY_KEY = ['billing', 'card'] as const;

/**
 * Card-on-file + subscription self-service, shown BELOW the plans grid and the
 * trust strip (and above the consent line) for a signed-in paid subscriber in
 * the recurring rollout. Covers the actionable set: next-charge preview,
 * auto-renew toggle, cancel-at-period-end, change / remove card, and
 * card-declined recovery. Payment history (reference, not action) lives in its
 * own `PaymentHistory` panel below.
 *
 * It sat ABOVE the grid until 2026-09-19. Ordering the auto-renew switch last
 * INSIDE the panel did not move it for a real subscriber, because the panel was
 * the first thing on /plans — see the comment at the call site in
 * `routes/plans.tsx`.
 */
export function SubscriptionManagement() {
  const t = useT();
  const { locale } = useLocale();
  const { status, user, refresh } = useAuth();
  const queryClient = useQueryClient();
  // Both flows attach a card (subscribe mode isn't used here — the user already
  // paid); a re-attach overwrites the unique (user, provider) card in place.
  const [modalOpen, setModalOpen] = useState(false);
  // «Изменить карту» is a plain card swap — it must NOT force auto-renew on;
  // attach / declined-recovery DO opt in. Track which intent opened the modal.
  const [modalKeepAutoRenew, setModalKeepAutoRenew] = useState(false);
  const openCardModal = (keepAutoRenew: boolean) => {
    setModalKeepAutoRenew(keepAutoRenew);
    setModalOpen(true);
  };

  const hasActivePaidSub = Boolean(user?.subscription && !user.subscription.expired);
  /* «Пауза на 1 месяц» (09.10): while the pause runs Pro is closed and the
     session carries no `subscription` — the panel must still render, because
     it is where «Пауза до …» and «Возобновить сейчас» live. */
  const sessionPause = user?.subscriptionPause ?? null;
  const enabled = status === 'authed' && (hasActivePaidSub || Boolean(sessionPause));

  const { data } = useQuery({
    queryKey: CARD_QUERY_KEY,
    queryFn: fetchSavedCard,
    enabled,
  });

  const { data: plans } = useQuery({ queryKey: ['plans'], queryFn: fetchPlans, enabled });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: CARD_QUERY_KEY });

  /*
   * `?attach=card` — the renewal reminder's deep link for a subscriber with NO
   * card (renewal-reminders.ts sets it when auto-renew is off). Measured
   * 2026-09-08: all 30 pro-monthly lapses of the previous month had
   * auto_renew=false, and the reminder could only send them here to buy again.
   * Opening the modal on arrival turns that reminder into the ask it should
   * have been. Gated on the server's card state so a user who attached a card
   * between the send and the click is not asked twice, and fired once per
   * mount so a refetch is not a second ask. Read from `window.location`, same
   * as the `?src` handling on /plans — Telegram opens this in a fresh tab.
   */
  const attachAsked = useRef(false);
  const sectionRef = useRef<HTMLElement>(null);
  // Own, paid subscription only — same reasoning as the /billing/return ask: a
  // team member's session carries the OWNER's subscription, and a grant has
  // no buyer to charge (see billing-return.tsx). The reminder never links a
  // team member or a grant here, but a forwarded link can land on either.
  const ownPaidSub = user?.subscription?.via === 'own' && user.subscription.source === 'payment';
  useEffect(() => {
    if (
      STRIPE_CHECKOUT ||
      attachAsked.current ||
      !ownPaidSub ||
      !data?.recurringEnabled ||
      data.card
    )
      return;
    if (new URLSearchParams(window.location.search).get('attach') !== 'card') return;
    attachAsked.current = true;
    track('card_attach_prompt_shown', { context: 'renewal' });
    openCardModal(false);
    /*
     * The panel moved below the plans grid (2026-09-19), so the deep link's
     * destination is off-screen on arrival: dismissing the modal used to leave
     * the reader looking at the panel, and now leaves him at the top of the
     * page with no sign of what the link was for. Scroll it into view behind
     * the modal.
     *
     * `scrollIntoView` on the SECTION, not on `window`: the app shell never
     * scrolls the window — `<main>` is the scroll container
     * (`app-layout.tsx`, `overflow-y-auto`), and the browser walks up to it.
     * Guarded because jsdom does not implement the method at all.
     */
    sectionRef.current?.scrollIntoView?.({ block: 'center' });
  }, [data, ownPaidSub]);

  /*
   * «Почему отключаете продление?» — which sheet the reason modal is asking
   * for, or null when it is closed (Igor, 2026-09-21: «Причину отписки
   * реализуем»).
   *
   * ⚠️ The two controls below do NOT mutate any more when they mean "off" —
   * they only open this. That is what makes «Оставить» free: the checkbox is
   * rendered from `data.autoRenew` (the SERVER's flag), never from local state,
   * so a sheet closed without an answer leaves it ticked with nothing to undo.
   * Turning auto-renew ON is untouched: there is no reason to ask why someone
   * is staying, and putting a dialog in front of it would be a retention wall
   * pointed the wrong way.
   */
  const [reasonFor, setReasonFor] = useState<ChurnReasonKind | null>(null);

  /*
   * The sheet's one value line (Igor, 10.10): «По вашим поискам за месяц
   * пришло N лотов». The sidebar badge's own count — lots matched in the last
   * 30 days that are still open, capped at 100 — fetched only while the sheet
   * is open. A failure or a zero simply leaves the line out.
   */
  const { data: monthMatches } = useQuery({
    queryKey: ['churn', 'month-matches'],
    queryFn: () => fetchNewMatchCount(new Date(Date.now() - 30 * 86_400_000)),
    enabled: reasonFor !== null,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const autoRenewMutation = useMutation({
    mutationFn: (vars: { enabled: boolean } & ChurnReasonInput) => setAutoRenew(vars.enabled, vars),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (vars: ChurnReasonInput) => removeSavedCard(vars),
    onSuccess: invalidate,
  });

  /* «Пауза на 1 месяц» (2026-10-09): the period end moves, so the session's
     `currentPeriodEnd` (/auth/me) is re-read as well as the card state. */
  const pauseMutation = useMutation({
    mutationFn: (vars: ChurnReasonInput) => pauseSubscription(vars),
    onSuccess: () => {
      void invalidate();
      void refresh();
    },
  });

  /* «Возобновить сейчас» / «Отменить паузу»: access and the period end both
     change, so the session is re-read with the card state. */
  const resumeMutation = useMutation({
    mutationFn: () => resumeSubscription(),
    onSuccess: () => {
      void invalidate();
      void refresh();
    },
  });

  function pauseFromSheet(reason: string | null, reasonText: string): void {
    pauseMutation.mutate(
      { reason: reason ?? undefined, reasonText: reasonText || undefined },
      { onSettled: () => setReasonFor(null) },
    );
  }

  /**
   * The reason sheet was answered — run the gesture it intercepted.
   *
   * Closed on SETTLE, not on press: a failure closes it too, because the panel
   * behind it is the thing that shows the real state (the checkbox re-reads the
   * server's flag on invalidate) and a sheet left open over a failed request
   * reads as "press it again", which is how one cancel becomes three.
   */
  function confirmReason(reason: string, reasonText: string): void {
    /*
     * «Меняю карту» on the card sheet (Igor, 10.10: «сделать как будто
     * привязывает старую и меняет»). `DELETE /billing/card` also switches
     * auto-renew off, so a buyer who only wanted another card was left with
     * none and lost Pro on the charge day without a word. Now: no DELETE, the
     * sheet closes and the card-attach sheet opens exactly as «Изменить карту»
     * opens it — a re-attach overwrites the card in place, auto-renew is not
     * touched. Closed without a new card → the old card and auto-renew stay.
     * No `card_removed` is written because nothing is removed.
     */
    if (reasonFor === 'card' && reason === CHURN_REASON_CHANGING_CARD) {
      setReasonFor(null);
      openCardModal(true);
      return;
    }
    const churn = { reason, reasonText: reasonText || undefined };
    const done = { onSettled: () => setReasonFor(null) };
    if (reasonFor === 'card') removeMutation.mutate(churn, done);
    else autoRenewMutation.mutate({ enabled: false, ...churn }, done);
  }

  // Only for a signed-in paid subscriber who's in the recurring rollout.
  if (!enabled || !data?.recurringEnabled) return null;

  const busy =
    autoRenewMutation.isPending ||
    removeMutation.isPending ||
    pauseMutation.isPending ||
    resumeMutation.isPending;

  // Next charge / cancellation copy, derived from the live period end and the
  // price the charger will take: the locked-in (grandfathered, ADR 0014) price,
  // else the plan's list price — never the list price over a locked one.
  const periodEnd = user?.subscription?.currentPeriodEnd ?? null;
  const endDate = formatDeadline(periodEnd, locale);
  const currentPlan = plans?.find((p) => p.key === user?.subscription?.plan);
  const lockedPriceUzs = user?.subscription?.lockedPriceUzs ?? null;
  const nextChargeUzs = lockedPriceUzs ?? currentPlan?.priceUzs ?? null;
  const amountLabel =
    nextChargeUzs !== null ? `${formatBudget(String(nextChargeUzs))} ${t('plans.som')}` : null;
  const nextChargeLine =
    endDate && amountLabel
      ? t('billing.autoRenew.nextCharge', { date: endDate, amount: amountLabel })
      : endDate
        ? t('billing.autoRenew.nextChargeDate', { date: endDate })
        : null;
  const canceledLine = endDate
    ? t('billing.autoRenew.canceledUntil', { date: endDate })
    : t('billing.autoRenew.canceled');

  /*
   * «Пауза на 1 месяц» (Igor, 09.10 — a real pause: Pro closed for the month,
   * no charge, then the card is charged as usual and access reopens). The card
   * endpoint is the source; the session copy covers the first paint.
   * `running` — inside the window; otherwise it is scheduled from the period end.
   */
  const pause = data.pause ?? sessionPause;
  const pauseRunning = pause ? new Date(pause.startsAt).getTime() <= Date.now() : false;
  const pauseFrom = pause ? formatDeadline(pause.startsAt, locale) : null;
  const pauseUntil = pause ? formatDeadline(pause.until, locale) : null;
  const pauseLine =
    pause && pauseUntil
      ? pauseRunning
        ? t('billing.pause.running', { until: pauseUntil })
        : t('billing.pause.scheduled', { from: pauseFrom ?? '', until: pauseUntil })
      : null;
  // The dates the sheet's pause note quotes — the server's window, formatted.
  const offerFrom = data.pauseOffer ? formatDeadline(data.pauseOffer.startsAt, locale) : null;
  const offerTo = data.pauseOffer ? formatDeadline(data.pauseOffer.until, locale) : null;
  const pauseDates = offerFrom && offerTo ? { from: offerFrom, to: offerTo } : null;

  // No bottom margin on the section: the panel is a grid child now, and the
  // grid's own `gap-4` spaces it from the consent line under it.
  return (
    <section
      ref={sectionRef}
      className="rounded-xl border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
    >
      <h2 className="text-[15px] font-semibold text-ink">{t('billing.subscription.title')}</h2>

      {/* Subscription status — always shown for a paid subscriber (card or not):
          next charge when auto-renew is on, else «active until X, won't renew».
          This is what a granted / one-time subscriber sees instead of a dead panel. */}
      {pauseLine ? (
        <div className="mt-2 rounded-md border border-edge bg-bone p-4">
          <p className="text-[13px] font-semibold leading-snug text-ink">{pauseLine}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => resumeMutation.mutate()}
            className="mt-3 inline-flex h-10 items-center justify-center gap-2 rounded-md bg-brand px-4 text-[13.5px] font-bold text-white transition-colors hover:bg-brand-mid disabled:opacity-60"
          >
            {resumeMutation.isPending ? <Spinner size="sm" /> : null}
            {pauseRunning ? t('billing.pause.resume') : t('billing.pause.cancel')}
          </button>
          {pauseRunning ? (
            <p className="mt-1 text-[12px] leading-snug text-ink-soft">
              {t('billing.pause.resumeNote')}
            </p>
          ) : null}
        </div>
      ) : (
        <p className="mt-1 text-[13px] font-medium text-ink">
          {data.autoRenew && nextChargeLine ? nextChargeLine : canceledLine}
        </p>
      )}

      {data?.card ? (
        <>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-[13px] text-ink-soft">
              {t('billing.autoRenew.card', { pan: data.card.maskedPan })}
            </p>
            {!STRIPE_CHECKOUT && (
              <button
                type="button"
                onClick={() => openCardModal(true)}
                className="text-[13px] font-semibold text-brand transition-colors hover:text-brand-mid"
              >
                {t('billing.autoRenew.changeCard')}
              </button>
            )}
          </div>

          {/* Card-declined recovery — the charger turned auto-renew off after a
              failed charge. Prompt a re-attach to resume auto-renewal. */}
          {!STRIPE_CHECKOUT && data.renewalFailed && (
            <div className="mt-4 rounded-md border border-status-closing-text/30 bg-status-closing-bg p-4">
              <p className="text-[13px] font-semibold leading-snug text-status-closing-text">
                {t('billing.autoRenew.declined')}
              </p>
              <button
                type="button"
                onClick={() => openCardModal(false)}
                className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-brand px-4 text-[13.5px] font-bold text-white transition-colors hover:bg-brand-mid"
              >
                {t('billing.autoRenew.updateCard')}
              </button>
            </div>
          )}

          {/* Cancelling = un-checking auto-renew BELOW (subscription stays active
              to period end). No separate «Отменить подписку» button — it was a
              second control for the same action. Only «Удалить карту» remains. */}
          <div className={STRIPE_CHECKOUT ? 'hidden' : 'mt-4 flex flex-wrap items-center gap-3'}>
            <button
              type="button"
              disabled={busy || STRIPE_CHECKOUT}
              /* Asks first — «Почему удаляете карту?» — and deletes only once
                 the sheet has an answer. The three card-only codes it prepends
                 exist because the commonest one («Меняю карту») is not churn
                 at all, and folding it into «Другое» would make the breakdown
                 read as a product failure. */
              onClick={() => setReasonFor('card')}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-edge bg-bone-surface px-4 text-[14px] font-semibold text-ink transition-colors hover:bg-bone disabled:opacity-60"
            >
              {removeMutation.isPending ? <Spinner size="sm" /> : null}
              {t('billing.autoRenew.remove')}
            </button>
          </div>

          {/*
           * Auto-renew sits LAST in the panel (2026-09-19, Igor). It used to be
           * the control directly under the card row, which put the one action
           * that ends a subscription above everything that argues for keeping
           * it; now the reader passes the period, the price, the card and the
           * card controls before reaching it.
           *
           * Behaviour is untouched — same `setAutoRenew` mutation, same
           * `busy` gate, same `aria-label`. The server owns the
           * `auto_renew_disabled` event and its `source` / `via` meta
           * (`subscriptions.service.ts`); this component has never passed
           * either, and moving the control must not start.
           *
           * `retention` is the argument for leaving it on, stated in the
           * concrete: what keeps running, what you do not have to re-enter,
           * and what the plan becomes if you switch it off. It renders only
           * with a real period end — the sentence names the date, and a line
           * that says a plan ends "on null" is worse than no line.
           */}
          <label className="mt-5 flex items-start gap-3 border-t border-edge pt-4">
            <Checkbox
              checked={data.autoRenew}
              disabled={busy}
              /* ON goes straight through; OFF opens the reason sheet and
                 mutates nothing until it is answered. The box is rendered from
                 the SERVER's flag, so an unanswered sheet leaves it ticked —
                 there is no optimistic state to roll back. */
              onCheckedChange={(v) =>
                v === true ? autoRenewMutation.mutate({ enabled: true }) : setReasonFor('autoRenew')
              }
              aria-label={t('billing.autoRenew.toggle')}
            />
            <span className="text-[13px] leading-snug text-ink">
              {t('billing.autoRenew.toggle')}
              {/* On a pause (10.10, Igor's phone pass) the period-end wording
                  was wrong twice: access closes at the pause START and the
                  charge comes at its END, while «тариф станет разовым и
                  закончится …» quoted the charge date as the last day. One
                  pause-specific line replaces both. */}
              <span className="mt-0.5 block text-[12px] text-ink-soft">
                {pause && pauseUntil
                  ? t('billing.autoRenew.notePaused', { date: pauseUntil })
                  : t('billing.autoRenew.note')}
              </span>
              {endDate && !(pause && pauseUntil) ? (
                <span className="mt-1 block text-[12px] leading-snug text-ink-soft">
                  {t('billing.autoRenew.retention', { date: endDate })}
                </span>
              ) : null}
            </span>
          </label>
        </>
      ) : STRIPE_CHECKOUT ? null : (
        <>
          <p className="mt-1 text-[13px] text-ink-soft">{t('billing.autoRenew.addPrompt')}</p>
          <button
            type="button"
            onClick={() => openCardModal(false)}
            className="mt-4 inline-flex h-11 items-center justify-center rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid"
          >
            {t('billing.autoRenew.addCard')}
          </button>
        </>
      )}

      <CardSaveModal
        open={modalOpen && !STRIPE_CHECKOUT}
        keepAutoRenew={modalKeepAutoRenew}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false);
          invalidate();
        }}
      />

      <ChurnReasonModal
        open={reasonFor !== null}
        /* `?? 'autoRenew'` only feeds the CLOSED sheet: Radix keeps the node
           mounted for its exit animation, and reading a null kind there would
           crash the panel on the way out rather than on the way in. */
        kind={reasonFor ?? 'autoRenew'}
        pending={busy}
        onCancel={() => setReasonFor(null)}
        onConfirm={confirmReason}
        matchCount={monthMatches ?? null}
        /* Only on the auto-renew sheet (deleting the card is not a pause), and
           only when the server says this subscription may still pause. */
        onPause={reasonFor === 'autoRenew' && data.pauseAvailable ? pauseFromSheet : undefined}
        pausePending={pauseMutation.isPending}
        pauseDates={pauseDates}
      />
    </section>
  );
}
